import React, { useState, ChangeEvent, useCallback, FormEvent } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import {
  Alert,
  AlertDescription,
  Field,
  Icon,
  TextInput,
} from "@zapier/design-system";
import { Button, IconButton } from "@zapier/design-system-beta";
import {
  emitAiPredictionUserFeedbackEventAtom,
  zslFeedbackPredictionIdAtom,
} from "@zapier/canvas-state";
import { HELPFUL, NOT_HELPFUL } from "@zapier/canvas-constants";
import styles from "./TemplateFeedback.module.css";

type Props = {
  onClose: () => void;
};

type Rating = typeof HELPFUL | typeof NOT_HELPFUL | null;

export function TemplateFeedback({ onClose }: Props) {
  const [userRating, setUserRating] = useState<Rating>(null);
  const [userFeedback, setUserFeedback] = useState<string>("");
  const [showThankYouAlert, setShowThankYouAlert] = useState(false);
  const predictionId = useAtomValue(zslFeedbackPredictionIdAtom);
  const emitAiPredictionUserFeedbackEvent = useSetAtom(
    emitAiPredictionUserFeedbackEventAtom,
  );

  const handleCommentChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setUserFeedback(event.target.value);
    },
    [],
  );

  const resetAll = useCallback(() => {
    setUserRating(null);
    setUserFeedback("");
  }, [setUserRating, setUserFeedback]);

  const handleFormSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      emitAiPredictionUserFeedbackEvent({
        prediction_id: predictionId,
        correlation_id: null,
        user_rating: userRating,
        user_feedback: userFeedback,
      });

      setShowThankYouAlert(true);
      resetAll();
      setTimeout(onClose, 3000);
    },
    [
      emitAiPredictionUserFeedbackEvent,
      predictionId,
      userRating,
      userFeedback,
      resetAll,
      onClose,
    ],
  );

  const handleClose = useCallback(() => {
    resetAll();
    onClose();
  }, [onClose, resetAll]);

  if (showThankYouAlert) {
    return (
      <div className={styles.successWrapper}>
        <Alert title="Thank you!" status="success">
          <AlertDescription>We appreciate your feedback.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.closeButtonWrapper}>
        <IconButton
          aria-label="Dismiss"
          name="formX"
          onPress={handleClose}
          variant="ghost"
        />
      </div>
      <h2 className={styles.header}>Was this a helpful suggestion?</h2>
      <form onSubmit={handleFormSubmit} className={styles.form}>
        <div className={styles.buttons}>
          <Button
            variant={userRating === HELPFUL ? "primary" : "tertiary"}
            size="small"
            onPress={() => setUserRating(HELPFUL)}
          >
            <Icon name="ratingThumbsUp" size={30} />
            Yes
          </Button>
          <Button
            variant={userRating === NOT_HELPFUL ? "primary" : "tertiary"}
            size="small"
            onPress={() => setUserRating(NOT_HELPFUL)}
          >
            <Icon name="ratingThumbsDown" size={30} />
            No
          </Button>
        </div>
        <Field
          label="Comments"
          renderInput={(inputProps) => (
            <TextInput
              {...inputProps}
              isMultiline={true}
              name="comments"
              placeholder="Let us know how we can improve"
              rows={2}
              onChange={handleCommentChange}
            />
          )}
        />
        <Button
          type="submit"
          variant="primary"
          size="small"
          isDisabled={!userFeedback && userRating === null}
        >
          Submit feedback
        </Button>
      </form>
    </div>
  );
}
